/**
 * Web Speech `onresult` often sends cumulative transcripts: each result is the
 * whole utterance so far, not a delta. Concatenating them repeats the first words.
 */

function normalizeSpaces(text) {
  return String(text || "").replace(/\s+/g, " ").trim();
}

/**
 * Merge two utterance snapshots. Later text replaces earlier when it already
 * contains it as a prefix (Chrome cumulative results).
 * @param {string} previous
 * @param {string} next
 * @returns {string}
 */
export function mergeUtterance(previous, next) {
  const a = normalizeSpaces(previous);
  const b = normalizeSpaces(next);
  if (!a) return b;
  if (!b) return a;

  const al = a.toLowerCase();
  const bl = b.toLowerCase();
  if (bl.startsWith(al)) return b;
  if (al.startsWith(bl)) return a;

  const aWords = a.split(" ");
  const bWords = b.split(" ");
  const max = Math.min(aWords.length, bWords.length);
  for (let n = max; n > 0; n--) {
    const suffix = aWords.slice(-n).join(" ").toLowerCase();
    const prefix = bWords.slice(0, n).join(" ").toLowerCase();
    if (suffix === prefix) {
      return normalizeSpaces(`${aWords.slice(0, aWords.length - n).join(" ")} ${b}`);
    }
  }
  return `${a} ${b}`;
}

/**
 * Collapse STT stutter at the start ("the the the cat" → "the cat") and
 * runs of 3+ identical words anywhere.
 * @param {string} text
 * @returns {string}
 */
export function collapsePrefixRepeats(text) {
  const words = normalizeSpaces(text).split(" ").filter(Boolean);
  if (words.length < 2) return words.join(" ");

  const first = words[0].toLowerCase();
  let i = 1;
  while (i < words.length && words[i].toLowerCase() === first) i += 1;
  if (i > 1) words.splice(1, i - 1);

  const out = [];
  for (const word of words) {
    const n = out.length;
    if (
      n >= 2 &&
      out[n - 1].toLowerCase() === word.toLowerCase() &&
      out[n - 2].toLowerCase() === word.toLowerCase()
    ) {
      continue;
    }
    out.push(word);
  }
  return out.join(" ");
}

/**
 * Rebuild the current recognition session from the results list (replace, not append).
 * @param {ArrayLike<{ isFinal?: boolean, 0?: { transcript?: string } }>} results
 * @returns {{ committed: string, interim: string }}
 */
export function snapshotSpeechResults(results) {
  let committed = "";
  let interim = "";
  const list = results || [];
  for (let i = 0; i < list.length; i += 1) {
    const piece = normalizeSpaces(list[i]?.[0]?.transcript);
    if (!piece) continue;
    if (list[i].isFinal) committed = mergeUtterance(committed, piece);
    else interim = mergeUtterance(interim, piece);
  }
  return { committed, interim };
}

/**
 * One string for preview/stop: prefer the longer overlapping snapshot, never
 * concatenate final+interim of the same words.
 * @param {string} committed
 * @param {string} interim
 * @returns {string}
 */
export function bestSpeechText(committed, interim) {
  return collapsePrefixRepeats(mergeUtterance(committed, interim));
}
